/* eslint-disable rxjs/no-exposed-subjects */
import { BehaviorSubject, filter, take } from 'rxjs';
import * as am5 from '@amcharts/amcharts5';
import * as am5Exporting from '@amcharts/amcharts5/plugins/exporting';
import am5themes_Responsive from '@amcharts/amcharts5/themes/Responsive';
import * as markerjs2 from 'markerjs2';
import { IAnnotatorSettings } from '@amcharts/amcharts5/.internal/plugins/exporting/Annotator';

import { abbreviateNumber } from '../../Misc/helpers/formatting/number.helper';
import { Am5ChartConfiguration } from './am5-chart-configuration.model';
import { Am5ChartSettings } from './am5-chart-settings.model';
import { chartColors } from '../enums/chart-colors.enum';
import { ChartSeries } from './chart-series.model';
import { CustomLegendItem } from './custom-legend-item.model';
import { DataPointModel } from './data-point.model';
import { ReportBlockTypeEnum } from '../../../Common/Reports/enums/report-block-type.enum';
import { userAppPreferencesDefault } from '../../../Core/Users/data/user-app-preferences-default';
import { ChartAnnotationMarkerTypesEnum } from '../../../Common/Dialogs/components/ChartAnnotationDialog/chart-annotation-marker-types.enum';
import { Am5ChartAnnotator } from './am5-chart-annotator.model';
import { ChartModuleTypesEnum } from '../enums/chart-module-types.enum';


am5.addLicense('AM5C-4142-4569-2330-0172');
Object.defineProperty(markerjs2.Activator, 'isLicensed', {
    get: function() {
        return true;
    },
    enumerable: false,
    configurable: true
});

export abstract class Am5Chart {
    public element: HTMLElement;

    public root: am5.Root;
    public chartWrapper: HTMLDivElement;
    public legendWrapper: HTMLDivElement;

    public header: am5.Container;
    public titleLabel: am5.Label;
    public metadataContainer: am5.Container;
    public dateRangeLabel: am5.Label;
    public primarySeriesLabel: am5.Label;
    public secondarySeriesLabel: am5.Label;
    public noContentLabel: am5.Label;
    public exporting: am5Exporting.Exporting;
    public exportData: DataPointModel[];
    public annotator: Am5ChartAnnotator;
    public markerArea: any;
    public annotatorImage: HTMLImageElement;

    public amchart: am5.SerialChart;
    public chartType: typeof am5.Entity;
    public id: string;
    public type: ReportBlockTypeEnum;
    public series: ChartSeries[];
    public config: Am5ChartConfiguration;
    public settings: Am5ChartSettings;
    public responsive: am5themes_Responsive;
    public legend: am5.Legend;

    public rawData: DataPointModel[];

    public isPreview: boolean;
    public isSingleSeries: boolean = true;
    public overrideNoContent: boolean = false;
    public timeout: NodeJS.Timeout;
    public destroyed: boolean = false;

    public dataUpdated$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
    public tooltipLinkClicked$: BehaviorSubject<any> = new BehaviorSubject(null);
    public annotatorConfigured$: BehaviorSubject<boolean> = new BehaviorSubject(false);
    public customLegendReadyBound: () => void;

    readonly defaultChartColors = userAppPreferencesDefault.reportsPrefs.defaultChartColors;

    private readonly headerRowGutter = 10;

    private preExportDomWidth: string;
    private preExportDomHeight: string;

    constructor(
        series: ChartSeries[],
        config: Am5ChartConfiguration,
        settings: Am5ChartSettings
    ) {
        this.series = series;
        this.config = config;
        this.settings = settings;
    }

    public initChart(element: HTMLElement, chartType: typeof am5.Entity): void {
        this.destroyed = false;
        this.root = am5.Root.new(element, { accessible: true });

        if (!Number.isFinite(this.settings.width)) {
            this.settings.width = element.clientWidth || element.getBoundingClientRect().width || undefined;
        }

        const chart: am5.SerialChart = chartType.new(this.root, { layout: this.root.verticalLayout });

        this.amchart = this.root.container.children.push(chart);
        this.setContainerPadding();

        if (
            (this.config.header.hasHeader ?? true) &&
            this.settings.displaySettings &&
            !this.settings.displaySettings.hasNoElements()
        ) {
            this.header = am5.Container.new(this.root, {
                layout: this.root.verticalLayout,
                width: am5.p100,
                paddingTop: 0,
                marginBottom: 5
            });
            if (this.settings.displaySettings.title) {
                this.setTitle();
            }
            if (this.settings.displaySettings.dateRange || this.settings.displaySettings.hasSourceLink()) {
                this.setMetadata();
            }

            this.amchart.children.unshift(this.header);
            this.amchart.events.on('boundschanged', () => this.updateHeaderLabelMaxWidths());
        }
        this.setUpResponsiveness();
        this.addResponsiveRules();
        this.configureExporting();
        this.configureAnnotator();

    }

    setContainerPadding() {
        this.amchart.setAll({
            paddingTop: 15,
            paddingRight: 15,
            paddingBottom: 15,
            paddingLeft: 15
        });
        if (this.config.padding?.noPadding) {
            this.amchart.setAll({
                paddingTop: 0,
                paddingRight: 0,
                paddingBottom: 0,
                paddingLeft: 0
            });
        }
        if (this.config.moduleType !== ChartModuleTypesEnum.SHARED_REPORTS && this.settings.displaySettings?.hasNoElements()) {
            this.amchart.set('paddingTop', this.config.header?.headlessHeadingTop);
        }
        if (this.config.padding?.customPadding) {
            this.amchart.setAll({
                paddingTop: this.config.padding.customPadding.top,
                paddingRight: this.config.padding.customPadding.right,
                paddingBottom: this.config.padding.customPadding.bottom,
                paddingLeft: this.config.padding.customPadding.left
            });
        }
    }

    private setTitle(): void {
        if (!this.settings.titleLabelText) {
            return;
        }
        const titleLabelSettings: am5.ILabelSettings = {
            id: this.config.id + '_title',
            width: am5.p100,
            paddingTop: 0,
            paddingBottom: 0,
            paddingLeft: 0,
            oversizedBehavior: 'truncate',
            textAlign: this.config.header.titleLabel.alignment || 'start',
            fontFamily: 'Poppins, Arial, sans-serif',
            focusable: true,
            background: am5.Rectangle.new(this.root, {
                fill: am5.color('#ffffff'),
                fillOpacity: 0
            }),
            interactive: true,
            tooltipText: ' '
        };
        if (this.config.header.titleLabel.headingLevel) {
            titleLabelSettings.html = `
                <span role="heading" aria-level="${this.config.header.titleLabel.headingLevel}" style="margin: 0 0 5px;">
                    ${this.settings.titleLabelText}
                </span>`;
        } else {
            titleLabelSettings.text = this.settings.titleLabelText;
        }
        if (this.config.header.titleHtml) {
            titleLabelSettings.html = this.config.header.titleHtml;
        }
        titleLabelSettings.fill = this.config.header.titleLabel.color ?
            am5.color(this.config.header.titleLabel.color) : am5.color(chartColors.agprText);
        titleLabelSettings.fontSize = this.config.header.titleLabel.fontSize ?
            this.config.header.titleLabel.fontSize : '1rem';
        titleLabelSettings.fontWeight = this.config.header.titleLabel.fontWeight ?
            this.config.header.titleLabel.fontWeight : '800';
        this.titleLabel = am5.Label.new(this.root, titleLabelSettings);
        if (this.config.header.titleHtml) {
            this.titleLabel.adapters.add('html', (text, target) => {
                return this.config.header.titleHtml.replace('{{width}}', (target.width() + 15) + 'px');
            });
        }

        const titleTooltipSettings: am5.ITooltipSettings = {
            labelText: this.settings.titleLabelText,
            getFillFromSprite: false,
            dy: 10,
            autoTextColor: false,
            pointerOrientation: 'up'
        };
        const titleTooltip = am5.Tooltip.new(this.root, titleTooltipSettings);
        titleTooltip.label.set('fill', am5.color(chartColors.agprText));
        titleTooltip.get('background').setAll({
            fill: am5.color(chartColors.agprWhite),
            strokeOpacity: 1,
            fillOpacity: 1,
            stroke: am5.color('#ddd'),
        });

        this.titleLabel.set('tooltip', titleTooltip);

        this.header.children.push(this.titleLabel);
    }

    private setMetadata(): void {
        this.metadataContainer = am5.Container.new(this.root, {
            width: am5.p100,
            layout: this.root.horizontalLayout,
            paddingTop: 2,
            paddingBottom: 8
        });

        if (this.settings.displaySettings.dateRange) {
            this.setDateRange();
        }
        if (this.settings.displaySettings.hasSourceLink()) {
            if (this.settings.displaySettings.dateRange) {
                this.setMetadataContainerSpacer();
            }
            this.setSeriesLabel();
        }

        this.header.children.push(this.metadataContainer);
    }

    private setDateRange(): void {
        const dateRangeLabelSettings: am5.ILabelSettings = {
            // width: this.isSingleSeries ? am5.p50 : am5.p100,
            maxWidth: this.settings.width * this.getDateRangeLabelWidthFraction(),
            oversizedBehavior: 'truncate',
            fontFamily: 'Poppins, Arial, sans-serif',
            text: this.config.header.dateRangeLabelText,
            focusable: true,
            fontSize: '0.781rem',
            textAlign: 'left',
            marginBottom: this.config.header.dateRangeLabelMarginBottom || 0,
            paddingTop: 0,
            paddingBottom: 0,
            paddingLeft: 0,
            background: am5.Rectangle.new(this.root, {
                fill: am5.color('#ffffff'),
                fillOpacity: 0
            }),
            interactive: true,
            tooltipText: ' '
        };
        this.dateRangeLabel = am5.Label.new(this.root, dateRangeLabelSettings);

        const dateRangeTooltipSettings: am5.ITooltipSettings = {
            getFillFromSprite: false,
            labelText: this.config.header.dateRangeLabelText,
            dy: 10,
            pointerOrientation: 'up',
            autoTextColor: false
        };
        const dateRangeTooltip = am5.Tooltip.new(this.root, dateRangeTooltipSettings);
        dateRangeTooltip.label.set('fill', am5.color(chartColors.agprText));
        dateRangeTooltip.get('background').setAll({
            fill: am5.color(chartColors.agprWhite),
            strokeOpacity: 1,
            fillOpacity: 1,
            stroke: am5.color('#ddd')
        });
        this.dateRangeLabel.set('tooltip', dateRangeTooltip);

        this.metadataContainer.children.push(this.dateRangeLabel);
    }

    private setMetadataContainerSpacer(): void {
        if (this.metadataContainer.get('layout') === this.root.horizontalLayout) {
            const spacer = am5.Container.new(this.root, {
                width: am5.p100
            });
            this.metadataContainer.children.push(spacer);
        }
    }

    private setSeriesLabel(): void {
        // there can be up to two series labels/links on the chart, figure those out now
        const seriesLabelParent = this.settings.displaySettings?.dateRange
            ? this.metadataContainer.children.push(am5.Container.new(this.root, {
                layout: this.root.horizontalLayout,
                position: 'absolute',
                x: am5.p100,
                centerX: am5.p100,
                ...(this.config.header.alignSeriesLabelWithDateRange ? { paddingTop: this.metadataContainer.get('paddingTop', 0) + 2 } : {})
            }))
            : this.metadataContainer;
        const primaryText = this.config.header.primarySeriesLabelText;
        const primaryUrl = this.config.header.primarySeriesUrl;
        const labelWidthFraction = this.getSeriesLabelWidthFraction();
        if (primaryText) {
            this.primarySeriesLabel = this.makeSeriesLabel(
                seriesLabelParent,
                primaryText,
                primaryUrl,
                'source',
                this.settings.displaySettings?.displaySourceAsLink,
                labelWidthFraction
            );
            if (!this.settings.displaySettings?.dateRange) {
                this.primarySeriesLabel.set('textAlign', 'start');
            }
        }
        if (this.config.header.secondarySeriesLabelText) {
            this.secondarySeriesLabel = this.makeSeriesLabel(
                seriesLabelParent,
                this.config.header.secondarySeriesLabelText,
                this.config.header.secondarySeriesUrl,
                'source2',
                this.settings.displaySettings?.displaySourceAsLink,
                labelWidthFraction
            );
            this.secondarySeriesLabel.setAll({
                marginLeft: 10
            });
            if (!this.settings.displaySettings?.dateRange) {
                this.secondarySeriesLabel.set('textAlign', 'start');
            }
        }
    }

    private hasSeriesLabelContent(): boolean {
        return !!this.config.header.primarySeriesLabelText || !!this.config.header.secondarySeriesLabelText;
    }

    private getDateRangeLabelWidthFraction(): number {
        return this.hasSeriesLabelContent() ? 0.5 : 1;
    }

    private getSeriesLabelWidthFraction(): number {
        const availableWidthFraction = this.settings.displaySettings?.dateRange ? 0.5 : 1;
        return this.config.header.secondarySeriesLabelText ? availableWidthFraction / 2 : availableWidthFraction;
    }

    private updateHeaderLabelMaxWidths(): void {
        const width = this.amchart?.innerWidth();
        if (!Number.isFinite(width) || width <= 0) {
            return;
        }
        const hasBothZones = !!this.settings.displaySettings?.dateRange && this.hasSeriesLabelContent();
        const availableWidth = hasBothZones ? width - this.headerRowGutter : width;
        this.dateRangeLabel?.set('maxWidth', availableWidth * this.getDateRangeLabelWidthFraction());
        const labelWidthFraction = this.getSeriesLabelWidthFraction();
        this.primarySeriesLabel?.set('maxWidth', availableWidth * labelWidthFraction);
        this.secondarySeriesLabel?.set('maxWidth', availableWidth * labelWidthFraction);
    }

    private makeSeriesLabel(
        parent: am5.Container,
        labelText: string,
        url?: string,
        id?: string,
        displaySourceAsLink?: boolean,
        maxWidthFraction: number = 0.5
    ): am5.Label {
        const isLink = displaySourceAsLink && !!url;
        const seriesLabelSettings: am5.ILabelSettings = {
            id: id,
            text: labelText,
            fontFamily: 'Poppins, Arial, sans-serif',
            fontSize: '0.781rem',
            fill: am5.color(chartColors.agprInteractive),
            textAlign: 'right',
            textDecoration: isLink ? 'underline' : undefined,
            focusable: true,
            maxWidth: this.settings.width * maxWidthFraction,
            oversizedBehavior: 'truncate',
            paddingTop: 0,
            paddingRight: 0,
            paddingBottom: 0,
            interactive: true,
            cursorOverStyle: isLink ? 'pointer' : 'default',
            tooltipText: ' ',
            background: am5.Rectangle.new(this.root, {
                fill: am5.color('#ffffff'),
                fillOpacity: 0
            }),
        };
        const seriesLabel = am5.Label.new(this.root, seriesLabelSettings);
        if (isLink) {
            seriesLabel.events.on('click', () => {
                window.open(url, '_blank');
            });
        }

        const seriesLabelTooltipSettings: am5.ITooltipSettings = {
            getFillFromSprite: false,
            labelText: labelText,
            dy: 10,
            pointerOrientation: 'up',
            autoTextColor: false
        };
        const seriesLabelTooltip = am5.Tooltip.new(this.root, seriesLabelTooltipSettings);
        seriesLabelTooltip.label.set('fill', am5.color(chartColors.agprText));
        seriesLabelTooltip.get('background').setAll({
            fill: am5.color(chartColors.agprWhite),
            strokeOpacity: 1,
            fillOpacity: 1,
            stroke: am5.color('#ddd')
        });
        seriesLabel.set('tooltip', seriesLabelTooltip);
        parent.children.push(seriesLabel);

        return seriesLabel;
    }

    setUpResponsiveness(): void {
        this.responsive = am5themes_Responsive.newEmpty(this.root);

        // TODO: add the header label tooltips and add responsiveness of the tooltips here
        // this.responsive.addRule({\);

        this.root.setThemes([
            this.responsive
        ]);
    }

    addResponsiveRules(): void {
        this.settings.responsiveRules?.forEach(rule => {
            this.responsive.addRule({
                name: rule.name,
                relevant: rule.relevant,
                tags: rule.tags,
                settings: rule.settings,
                applying: rule.applying?.bind(this),
                removing: rule.removing?.bind(this)
            });
        });
    }

    public configureExporting(): void {
        this.exporting = am5Exporting.Exporting.new(this.root, {
            pngOptions: {
                quality: 0.9
            },
            jpgOptions: {
                quality: 0.9
            }
        });

    }

    configureAnnotator(): void {
        const annotatorSettings: IAnnotatorSettings = {
            markerSettings: {
                availableMarkerTypes: Object.values(ChartAnnotationMarkerTypesEnum),
                uiOffsetTop: 0
            },
            markerStyleSettings: {
                redoButtonVisible: true,
                toolbarColor: chartColors.agprText,
                toolbarBackgroundColor: chartColors.agprWhite,
                toolbarBackgroundHoverColor: chartColors.agprGreyBackground,
                toolbarHeight: 50
            }
        };

        this.annotator = Am5ChartAnnotator.new(this.root, annotatorSettings);
        this.annotatorConfigured$.next(true);
    }

    /**
     * Show 'No Content to Display' message
     */
    public showNoContentMessage(message?: string): void {
        if (this.noContentLabel) {
            this.noContentLabel.dispose();
        }
        if (this.settings.displaySettings?.hasNoElements()) {
            this.root.container.set('paddingTop', 0);
        }

        let noContentLabelSettings: am5.ILabelSettings;
        // social styles not tested
        if (this.config.noContent.isMessageStyle) {
            noContentLabelSettings = {
                width: am5.p100,
                paddingTop: 0,
                paddingRight: 0,
                paddingBottom: 0,
                paddingLeft: 0,
                fontFamily: 'Poppins, Arial, sans-serif',
                fontSize: '0.875rem',
                fill: am5.color(chartColors.agprGreyTextPlaceholder),
                isMeasured: false,
                html: message ? message :
                    `<div style="display:flex; align-items:center; min-width: 200px; border: 1px solid ${chartColors.agprGreyBorder}; border-radius: 8px;`
                        + `background-color: ${chartColors.agprGreyBackground}; padding: 10px; margin: 10px">`
                        + '<span class="fa-duotone fa-regular fa-file-xmark" style="font-size:1.25rem; margin-right:10px; color:'
                        + chartColors.agprGreyIcon
                        + ';"></span>'
                        + this.config.noContent.label
                        + '</div>'
            };
        } else {
            noContentLabelSettings = {
                text: message || this.config.noContent.label,
                width: am5.p100,
                textAlign: 'center',
                // x: am5.p50,
                // centerX: am5.p50,
                y: am5.p50,
                fill: am5.color(chartColors.agprGrey),
                fontFamily: 'Poppins, Arial, sans-serif',
                fontSize: '0.813rem',
                isMeasured: false
            };
        }
        this.noContentLabel = am5.Label.new(this.root, noContentLabelSettings);
        this.root.container.children.push(this.noContentLabel);

    }

    public hideNoContentMessage(): void {
        if (this.noContentLabel) {
            this.noContentLabel.dispose();
            this.noContentLabel = null;
        }
    }

    public initCustomLegend(): void {
        this.legendWrapper = document.createElement('div');
        this.legendWrapper.className = 'agpr-customLegend';

        this.config.customLegend.legendContainerElement.innerHTML = '';
        this.config.customLegend.legendContainerElement.appendChild(this.legendWrapper);


        this.dataUpdated$.pipe(filter(x => !!x), take(1)).subscribe(() => {
            this.updateCustomLegend();
        });
    }

    public updateCustomLegend(): void {
        if (!this.legendWrapper) {
            return;
        }

        this.legendWrapper.innerHTML = '';
        const snippet = document.createDocumentFragment();
        let items = this.getCustomLegendItems();
        if (this.config.customLegend?.getCustomLegendItems) {
            items = this.config.customLegend.getCustomLegendItems(this);
        }
        if (items?.length) {
            items.forEach((item) => {
                snippet.append(this.getCustomLegendItemElement(item));
            });
            this.legendWrapper.append(snippet);
        }
    }

    public getCustomLegendItems(): CustomLegendItem[] {
        return [];
    }

    public getCustomLegendItemElement(dataPoint: CustomLegendItem): HTMLElement {
        const item = document.createElement('div');
        item.classList.add('agpr-customLegend-item');
        let html = `
            <span style='background: ${dataPoint.color};' class='agpr-customLegend-itemMarker'></span>
            <span class='agpr-customLegend-itemLabel'>${dataPoint.label}</span>
        `;
        if (dataPoint.count != null) {
            html += `&nbsp;-&nbsp;<span class='agpr-customLegend-itemCount'>${abbreviateNumber(dataPoint.count, { decimals: 2 })}</span>`;
        }
        if (dataPoint.percent != null) {
            html += `&nbsp;-&nbsp;<span class='agpr-customLegend-itemPercent'>${dataPoint.percent}%</span>`;
        }
        if (dataPoint.mouseover) {
            item.addEventListener('mouseover', () => dataPoint.mouseover(dataPoint));
        }
        if (dataPoint.mouseout) {
            item.addEventListener('mouseout', () => dataPoint.mouseout(dataPoint));
        }
        if (dataPoint.click) {
            item.classList.add('agpr-customLegend-item--clickable');
            item.addEventListener('click', () => dataPoint.click(dataPoint));
        }
        item.innerHTML = html;

        return item;
    }

    public updateData(data: DataPointModel[]): void {
        this.rawData = data || [];
        this.exportData = data || [];
    }

    public updateIndicatorData(data: DataPointModel[]): void {
    }

    public async export(type: string, fileName: string): Promise<void> {
        this.exporting.set('filePrefix', fileName);
        switch (type) {
            case 'png':
            case 'jpg':
                await this.exporting.download(type);
                break;
            case 'csv':
            case 'json':
                this.exporting.set('dataSource', this.exportData);
                this.exporting.set('dataFields', this.config.getExportConfiguration(this).dataFields);
                await this.exporting.download(type);
                break;
            case 'print':
                await this.exporting.print();
                break;
        }
        this.restoreExportState();
    }

    protected restoreExportState(): void {
    }

    public async  getPNGImageData(
        width?: number,
        height?: number,
        transparent?: boolean,
        keepFontsize?: boolean,
        onExportResized?: (exportWidth: number, exportHeight: number) => void | Promise<void>,
        onExportRestored?: () => void
    ): Promise<string> {
        if (!this.exporting) {
            return '';
        }
        try {
            this.setPDFExportStyles(1, keepFontsize);
            let exportWidth = 840;
            let exportHeight = 540;
            if (width && height) {
                this.root.dom.style.width = width + 'px';
                this.root.dom.style.height = height + 'px';
                exportWidth = width;
                exportHeight = height;
            }
            this.resizeAndSettle();
            await onExportResized?.(exportWidth, exportHeight);
            await this.annotator?.waitForBake();
            if (transparent) {
                this.exporting.set('backgroundOpacity', 0);
            }
            return await this.exporting.export('png');
        } finally {
            if (transparent) {
                this.exporting.set('backgroundOpacity', 1);
            }
            this.unsetPDFExportStyles();
            this.resizeAndSettle();
            this.restoreExportState();
            onExportRestored?.();
        }
    }

    private resizeAndSettle(): void {
        this.root.resize();
        this.root._runTickerNow();
    }

    /**
     * Modifies the chart to have the right styles and be the right dimensions for export
     */
    public setPDFExportStyles(scale?: number, keepFontsize?: boolean): void {
        this.preExportDomWidth = this.root.dom.style.width;
        this.preExportDomHeight = this.root.dom.style.height;
        this.root.dom.style.width = 840 * scale + 'px';
        this.root.dom.style.height = 540 * scale + 'px';

        if (!keepFontsize) {
            if (this.titleLabel) {
                this.titleLabel.set('fontSize', 24);
            }
            if (this.dateRangeLabel) {
                this.dateRangeLabel.setAll({
                    fontSize: 16,
                    oversizedBehavior: 'none',
                    maxWidth: undefined
                });
            }
            if (this.primarySeriesLabel && this.isSingleSeries) {
                this.primarySeriesLabel.set('fontSize', 16);
            }
            if (this.secondarySeriesLabel) {
                this.secondarySeriesLabel.set('fontSize', 16);
            }
            if (this.noContentLabel) {
                this.noContentLabel.set('fontSize', 16);
            }
        }

        if (this.header) {
            this.header.set('marginBottom', 15);
        }
        if (this.primarySeriesLabel && this.isSingleSeries) {
            this.primarySeriesLabel.set('fill', am5.color(chartColors.agprBlack));
        }
        if (this.noContentLabel) {
            this.noContentLabel.set('dy', 50);
        }
    }

    /**
     * Undoes the changes made in setExportStyles to make the chart normal again
     */
    public unsetPDFExportStyles(keepFontsize?: boolean): void {
        this.root.dom.style.width = this.preExportDomWidth ?? '';
        this.root.dom.style.height = this.preExportDomHeight ?? '';

        if (!keepFontsize) {
            if (this.titleLabel) {
                this.titleLabel.set(
                    'fontSize',
                    this.config.header.titleLabel.fontSize || '1rem'
                );
            }
            if (this.dateRangeLabel) {
                this.dateRangeLabel.setAll({
                    fontSize: '0.781rem',
                    oversizedBehavior: 'truncate',
                    maxWidth: this.settings.width * this.getDateRangeLabelWidthFraction()
                });
            }
            if (this.primarySeriesLabel && this.isSingleSeries) {
                this.primarySeriesLabel.set('fontSize', '0.781rem');
            }
            if (this.secondarySeriesLabel) {
                this.secondarySeriesLabel.set('fontSize', '0.781rem');
            }
            if (this.noContentLabel) {
                this.noContentLabel.set('fontSize', '0.875rem');
            }
        }

        if (this.header) {
            this.header.set('marginBottom', 0);
        }
        if (this.primarySeriesLabel && this.isSingleSeries) {
            this.primarySeriesLabel.set('fill', am5.color(chartColors.agprInteractive));
        }
        if (this.noContentLabel) {
            this.noContentLabel.set('dy', 0);
        }
    }

    public abstract clone(): Am5Chart;
    public abstract isContentEmpty(): boolean;

    /**
     * Destroys the chart
     */
    public destroy() {
        this.destroyed = true;
        this.chartWrapper?.remove();
        this.legendWrapper?.remove();

        requestAnimationFrame(() => {
            this.root?.dispose();
            this.amchart = null;
        });
    }
}
